import {createSelector} from '@reduxjs/toolkit';
import {absurd} from 'fp-ts/lib/function';
import {TargetAccountRef, TargetRef} from '~/entities/operations-fiat';
import {CardId, Source} from '~/library/types';
import {FinancialData} from '~/redux/finances/financesReducer';
import {OperableAccount, OperableBank, OperableCard, OperableTarget, OperableWallet, OperationModel} from './domain';
import {selectFinancesResource} from './financesResource';

export const selectFinancesState = (state: AppModel) => state.finances;
export const selectFinancesDataStatus = (state: AppModel) => state.finances.status;
export const selectFinancesData = (state: AppModel) => state.finances.data;
export const getAllAccounts = (data: FinancialData) => Object.values(data.accounts);

export const getAllOperations = createSelector(
    (data: FinancialData) => data,
    (data): OperationModel[] => Object.values(groupOperationsByType(data)).flat()
);

export type OperationsGroupedByType = Record<OperationModel.Type, Array<OperationModel>>;
export const groupOperationsByType = (data: FinancialData): OperationsGroupedByType => {
    const result: OperationsGroupedByType = {exchange: [], topup: [], withdraw: []};
    for (const {type, accounts, targets} of unpackOperations(data)) {
        (result[type] || (result[type] = [])).push(
            ...accounts.flatMap(account =>
                targets
                    .filter(tgt => !(tgt.type === account.type && tgt.id === account.id))
                    .flatMap((target): OperationModel => ({type, account, target}))
            )
        );
    }
    return result;
};

type UnpackedOperations = Array<{
    type: OperationModel.Type;
    accounts: Array<OperableAccount>;
    targets: Array<OperableTarget>;
}>;

const unpackOperations = (data: FinancialData): UnpackedOperations =>
    data.operations.map(({type, accounts: accountRefs, targets: targetRefs}) => ({
        type,
        accounts: accountRefs.map(ref => assertFound(getTarget(data, ref), ref)),
        targets: targetRefs.map(ref => assertFound(getTarget(data, ref), ref)),
    }));

function getTarget(data: FinancialData, ref: TargetAccountRef): OperableAccount | undefined;
function getTarget(data: FinancialData, ref: TargetRef & {type: 'wallet'}): OperableWallet | undefined;
function getTarget(data: FinancialData, ref: TargetRef & {type: 'payment_network'}): OperableBank | undefined;
function getTarget(data: FinancialData, ref: TargetRef & {type: 'card'}): OperableCard | undefined;
function getTarget(data: FinancialData, ref: TargetRef): OperableTarget | undefined;
function getTarget(data: FinancialData, ref: TargetRef): OperableTarget | undefined {
    switch (ref.type) {
        case 'card':
            return getPaymentCard(data, ref);
        case 'payment_network':
            return getPaymentNetwork(data, ref);
        case 'crypto':
        case 'stablecoin':
        case 'fiat':
            return getAccount(data, ref);
        case 'wallet':
            return staticWalletStub;
        default:
            return absurd(ref.type);
    }
}

function assertFound<T extends OperableTarget>(target: T | undefined, ref: TargetRef) {
    if (target === undefined) {
        throw new Error(`Target '${ref.type}' with id '${JSON.stringify(ref.id)}' not found.`);
    }

    return target;
}

function getAccount(data: FinancialData, ref: TargetRef): OperableAccount | undefined {
    return data.accounts.find(acc => acc.id == ref.id && acc.type === ref.type);
}

function getPaymentCard(data: FinancialData, ref: TargetRef): OperableCard | undefined {
    //FIXME: find better way
    // card with id 0 means user can add new card
    // it's here because operation can contain card with id 0 but that card not presented in list of cards
    // this is just a placeholder
    if (ref.id === 0) {
        return {
            id: 0 as CardId,
            type: 'card',
            icon: '' as Source,
            name: 'New card',
            expirationDate: '00/00',
            holderFullName: '',
            numberFormatted: '',
            flags: [],
        };
    }

    return data.paymentCards.find(card => card.id === ref.id);
}

function getPaymentNetwork(data: FinancialData, ref: TargetRef): OperableBank | undefined {
    return data.paymentNetworks.find(bank => bank.id === ref.id);
}

const staticWalletStub: OperableWallet = {id: 0, type: 'wallet'};

export const selectBankResource = (bankId: string) => (state: AppModel) =>
    selectFinancesResource(state).mapSuccess(fin => getPaymentNetwork(fin, {type: 'payment_network', id: bankId}));

export const selectCardResource = (cardId: string) => (state: AppModel) =>
    selectFinancesResource(state).mapSuccess(fin => getPaymentCard(fin, {type: 'card', id: cardId}));
